import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  MenuItem,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { liveAnalyticsAPI } from '../../services/liveAnalyticsApi';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Analytics: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');

  // Live data queries
  const { data: enrollmentData, isLoading: enrollmentLoading } = useQuery({
    queryKey: ['liveEnrollmentStats', { district: selectedDistrict, school_level: selectedLevel }],
    queryFn: () => liveAnalyticsAPI.getLiveEnrollmentStats({ district: selectedDistrict, school_level: selectedLevel })
  });

  const { data: staffData, isLoading: staffLoading } = useQuery({
    queryKey: ['liveStaffStats', { district: selectedDistrict }],
    queryFn: () => liveAnalyticsAPI.getLiveStaffStats({ district: selectedDistrict })
  });

  const { data: infrastructureData } = useQuery({
    queryKey: ['liveInfrastructureStats', { district: selectedDistrict, school_level: selectedLevel }],
    queryFn: () => liveAnalyticsAPI.getLiveInfrastructureStats({ district: selectedDistrict, school_level: selectedLevel })
  });

  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: ['enrollmentTrends', { district: selectedDistrict, school_level: selectedLevel }],
    queryFn: () => liveAnalyticsAPI.getEnrollmentTrends({ district: selectedDistrict, school_level: selectedLevel })
  });

  const { data: districtData, isLoading: districtLoading } = useQuery({
    queryKey: ['districtComparison'],
    queryFn: () => liveAnalyticsAPI.getDistrictComparison()
  });

  const { data: schoolLevelsData } = useQuery({
    queryKey: ['schoolLevels'],
    queryFn: () => liveAnalyticsAPI.getSchoolLevelsList()
  });


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const enrollment = enrollmentData?.data || {};
  const staff = staffData?.data?.data || {};
  const infrastructure = infrastructureData?.data || {};
  const trends = Array.isArray(trendsData?.data?.trends) ? trendsData?.data?.trends : [];
  const districts = Array.isArray(districtData?.data?.data) ? districtData?.data?.data : Array.isArray(districtData?.data) ? districtData?.data : [];
  const schoolLevels = Array.isArray(schoolLevelsData?.data) ? schoolLevelsData?.data : [];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Analytics & Insights
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1" color="text.secondary">
            Comprehensive analysis from live SIS database
          </Typography>
          <Chip 
            label="Real-time Data" 
            color="success" 
            size="small" 
          />
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="District"
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Districts</MenuItem>
              {(districts || []).map((district: any) => (
                <MenuItem key={district.district_id} value={district.district_name}>
                  {district.district_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="School Level"
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Levels</MenuItem>
              {(schoolLevels || []).map((level: any) => (
                <MenuItem key={level.level_id} value={level.level_name}>
                  {level.level_name} ({level.school_count} schools)
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for different analytics views */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="analytics tabs">
          <Tab label="Enrollment Analysis" />
          <Tab label="Staff Analysis" />
          <Tab label="Infrastructure" />
          <Tab label="Trends & Patterns" />
          <Tab label="District Comparison" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Enrollment Statistics */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Enrollment Overview
                  </Typography>
                  {enrollmentLoading ? (
                    <CircularProgress />
                  ) : (
                    <Box>
                      <Typography variant="h4" color="primary">
                        {enrollment.total_students?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Students
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          Male: {enrollment.total_male?.toLocaleString() || 0} ({((enrollment.total_male || 0) / (enrollment.total_students || 1) * 100).toFixed(1)}%)
                        </Typography>
                        <Typography variant="body2">
                          Female: {enrollment.total_female?.toLocaleString() || 0} ({((enrollment.total_female || 0) / (enrollment.total_students || 1) * 100).toFixed(1)}%)
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Gender Distribution Chart */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Gender Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Male', value: enrollment.total_male || 0, fill: '#1976d2' },
                          { name: 'Female', value: enrollment.total_female || 0, fill: '#dc004e' }
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={60}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {[1, 2].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#1976d2' : '#dc004e'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            {/* Staff Statistics */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Staff Overview
                  </Typography>
                  {staffLoading ? (
                    <CircularProgress />
                  ) : (
                    <Box>
                      <Typography variant="h4" color="primary">
                        {staff.total_staff?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Staff Members
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          Teachers: {staff.teachers?.toLocaleString() || 0}
                        </Typography>
                        <Typography variant="body2">
                          Support Staff: {staff.support_staff?.toLocaleString() || 0}
                        </Typography>
                        <Typography variant="body2">
                          Male: {staff.male_staff?.toLocaleString() || 0} ({((staff.male_staff || 0) / (staff.total_staff || 1) * 100).toFixed(1)}%)
                        </Typography>
                        <Typography variant="body2">
                          Female: {staff.female_staff?.toLocaleString() || 0} ({((staff.female_staff || 0) / (staff.total_staff || 1) * 100).toFixed(1)}%)
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Student-Teacher Ratio */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Student-Teacher Ratio
                  </Typography>
                  <Typography variant="h4" color="secondary">
                    {staff.student_teacher_ratio || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Students per Teacher
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Staff Gender Distribution Chart */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Staff Gender Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Male', value: staff.male_staff || 0, fill: '#1976d2' },
                          { name: 'Female', value: staff.female_staff || 0, fill: '#dc004e' }
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={60}
                        dataKey="value"
                        label={({ name, percent }) => staff.male_staff || staff.female_staff ? `${name} ${(percent * 100).toFixed(0)}%` : 'No data'}
                      >
                        {[1, 2].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#1976d2' : '#dc004e'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Enrollment by Level Chart */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Enrollment by School Level
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={enrollment.by_level || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="school_level_name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="male_count" fill="#1976d2" name="Male" />
                      <Bar dataKey="female_count" fill="#dc004e" name="Female" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            {/* Infrastructure Stats */}
            {Object.entries(infrastructure).map(([key, value]: [string, any]) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {value?.percentage || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value?.with_access || 0} of {value?.total || 0} schools
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            {/* Enrollment Trends */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Enrollment Trends Over Time
                  </Typography>
                  {trendsLoading ? (
                    <CircularProgress />
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="total" stroke="#1976d2" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* AI Insights Panel */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    AI Insights & Analysis
                  </Typography>
                  {trendsLoading ? (
                    <CircularProgress />
                  ) : trendsData?.data?.ai_insights ? (
                    <Box>
                      {/* Growth Analysis */}
                      {trendsData.data.ai_insights.growth_analysis && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="primary">
                            Growth Analysis
                          </Typography>
                          <Typography variant="body2">
                            Trend: {trendsData.data.ai_insights.growth_analysis.trend}
                          </Typography>
                          <Typography variant="body2">
                            Avg Growth: {trendsData.data.ai_insights.growth_analysis.average_growth_rate}%
                          </Typography>
                        </Box>
                      )}

                      {/* Predictions */}
                      {trendsData.data.ai_insights.predictions?.next_year && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="secondary">
                            Predictions
                          </Typography>
                          <Typography variant="body2">
                            {trendsData.data.ai_insights.predictions.next_year.year}: {trendsData.data.ai_insights.predictions.next_year.predicted_enrollment?.toLocaleString()} students
                          </Typography>
                          <Typography variant="body2" fontSize="0.75rem" color="text.secondary">
                            Confidence: {trendsData.data.ai_insights.predictions.next_year.confidence}
                          </Typography>
                        </Box>
                      )}

                      {/* Patterns */}
                      {trendsData.data.ai_insights.patterns?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="warning.main">
                            Detected Patterns
                          </Typography>
                          {trendsData.data.ai_insights.patterns.slice(0, 2).map((pattern: any, index: number) => (
                            <Typography key={index} variant="body2" fontSize="0.75rem">
                              • {pattern.description}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      {/* Recommendations */}
                      {trendsData.data.ai_insights.recommendations?.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" color="success.main">
                            Recommendations
                          </Typography>
                          {trendsData.data.ai_insights.recommendations.slice(0, 2).map((rec: any, index: number) => (
                            <Typography key={index} variant="body2" fontSize="0.75rem">
                              • {rec.title}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      AI insights will appear here when trend data is available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Performance Indicators */}
            {trendsData?.data?.ai_insights?.performance_indicators && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Performance Indicators
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Box textAlign="center">
                          <Typography variant="h4" color="primary">
                            {Math.round(trendsData.data.ai_insights.performance_indicators.enrollment_health_score)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Health Score
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box textAlign="center">
                          <Typography variant="h4" color="secondary">
                            {trendsData.data.ai_insights.performance_indicators.trend_strength}/10
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Trend Strength
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box textAlign="center">
                          <Chip 
                            label={trendsData.data.ai_insights.performance_indicators.current_status}
                            color={
                              trendsData.data.ai_insights.performance_indicators.current_status === 'good' ? 'success' :
                              trendsData.data.ai_insights.performance_indicators.current_status === 'concerning' ? 'error' : 'default'
                            }
                            size="small"
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Current Status
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box textAlign="center">
                          <Chip 
                            label={trendsData.data.ai_insights.performance_indicators.data_quality}
                            color="info"
                            size="small"
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Data Quality
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            {/* District Summary Cards */}
            {districtData?.data?.summary && (
              <>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="primary">
                        {districtData.data.summary.total_districts}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Districts
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="secondary">
                        {districtData.data.summary.total_schools?.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Schools
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="success.main">
                        {districtData.data.summary.total_students?.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Students
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="warning.main">
                        {districtData.data.summary.average_student_teacher_ratio}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Avg S/T Ratio
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}

            {/* District Comparison Chart */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    District Performance Comparison
                  </Typography>
                  {districtLoading ? (
                    <CircularProgress />
                  ) : (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={(districts || []).slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="district_name" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip formatter={(value, name) => [
                          typeof value === 'number' ? value.toLocaleString() : value,
                          name
                        ]} />
                        <Bar dataKey="student_count" fill="#1976d2" name="Students" />
                        <Bar dataKey="school_count" fill="#2e7d32" name="Schools" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Top Districts Performance Table */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top 5 Districts by Enrollment
                  </Typography>
                  {districtLoading ? (
                    <CircularProgress />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {(districts || []).slice(0, 5).map((district: any, index: number) => (
                        <Paper key={district.district_id} variant="outlined" sx={{ p: 2 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={1}>
                              <Typography variant="h6" color="primary">
                                #{index + 1}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {district.district_name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {district.region_name}
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography variant="body2">
                                {district.student_count?.toLocaleString()} students
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography variant="body2">
                                {district.school_count} schools
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography variant="body2">
                                S/T Ratio: {district.student_teacher_ratio}
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Chip 
                                label={`${district.performance_score?.toFixed(0)} pts`}
                                color={district.performance_score > 70 ? 'success' : district.performance_score > 50 ? 'warning' : 'error'}
                                size="small"
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Analytics;